import React, { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Filters, createFieldDefs, SubmitEvent } from '@workboard/ui-list-filters';
import {
  Box, Button, Popover, ThemeProvider, createTheme, popoverClasses,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { mergeThemeOptions, themeOptions } from '@workboard/wobo-material';
import {
  TIME_PERIOD_OPERATOR, TeamOption, AlignmentFilterCriterion, reconcileChipCriteria,
} from './alignmentModalFilters.helpers';
import { ALIGNMENT_FILTER_STATUS } from '../../../../constants/okr/alignmentFilter.constants';

const StyledPopover = styled(Popover)({
  [`.${popoverClasses.paper}`]: {
    borderRadius: '8px',
    padding: '4px 16px 16px',
    marginTop: '5px',
    width: '650px',
    maxHeight: 'calc(100vh - 120px)',
    '& button:has(svg[data-testid="AddIcon"])': {
      display: 'none',
    },
  },
});

const filtersThemeExtensions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: { height: 'auto', minHeight: '40px' },
        input: { height: 'auto' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { height: 'auto' },
        input: { height: '24px', padding: '8.5px 14px' },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { height: 'auto', minHeight: '1.4375em' },
      },
    },
  },
};

const filtersTheme = createTheme(mergeThemeOptions(themeOptions, filtersThemeExtensions));

const FilterBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #0072ab;
    color: #ffffff;
    font-size: 11px;
    line-height: 1;
    width: 18px;
    height: 18px;
    margin-left: 6px;
    border-radius: 50%;
`;

const getAlignmentFieldDefs = (
  t: (key: string) => string,
  teamOptions: TeamOption[],
) => createFieldDefs({
  fields: {
    status: {
      label: t('Objective Status'),
      type: 'multi-select-button',
      operators: ['is'],
      options: () => Promise.resolve([
        { label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN },
        { label: t('Closed'), value: ALIGNMENT_FILTER_STATUS.CLOSED },
      ]),
    },
    timePeriod: {
      label: t('Time Period'),
      type: 'time-periods-select',
      operators: [TIME_PERIOD_OPERATOR.FALLS_WITHIN, TIME_PERIOD_OPERATOR.EXACTLY_EQUALS],
      fieldOptions: {
        halfYearTimePeriods: true,
        multiTimePeriods: true,
        enableAutoDefineUnknownTimePeriod: true,
      },
    },
    team: {
      label: t('Team'),
      type: 'multi-select',
      operators: ['is'],
      options: () => Promise.resolve(teamOptions),
    },
  },
});

type AlignmentModalFiltersProps = {
    appliedCount: number;
    initialValues: Partial<SubmitEvent['values']>;
    teamOptions: TeamOption[];
    onApply: (event: SubmitEvent) => void;
    onReset: () => void;
};

const AlignmentModalFilters: React.FC<AlignmentModalFiltersProps> = ({
  appliedCount,
  initialValues,
  teamOptions,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const closePanel = () => setOpen(false);

  const handleSubmit = (event: SubmitEvent) => {
    const shownLabels = new Set(
      Array.from(panelRef.current?.querySelectorAll('.MuiChip-label') ?? [])
        .map((el) => el.textContent?.trim())
        .filter((label): label is string => !!label),
    );
    const criteria = reconcileChipCriteria(
      (event?.values?.group?.criteria ?? []) as AlignmentFilterCriterion[],
      shownLabels,
    );
    const group = { ...event.values?.group, criteria };
    onApply({ ...event, values: { ...event.values, group } } as SubmitEvent);
    closePanel();
  };

  const handleReset = () => {
    onReset();
    closePanel();
  };

  const fieldDefs = useMemo(() => getAlignmentFieldDefs(t, teamOptions), [t, teamOptions]);

  return (
    <ThemeProvider theme={filtersTheme}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          ref={anchorRef}
          variant="outlined"
          disableElevation
          startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          aria-label={t('Filters')}
          aria-expanded={open}
          sx={{
            height: 28,
            paddingLeft: '14px !important',
            paddingRight: '14px !important',
            fontSize: 14,
            textTransform: 'none',
          }}
        >
          {t('Filters')}
          {appliedCount > 0 && (
            <FilterBadge aria-label={t('{{count}} filters applied', { count: appliedCount })}>
              {appliedCount}
            </FilterBadge>
          )}
        </Button>
      </Box>

      <StyledPopover
        anchorEl={anchorRef.current}
        open={open}
        disableScrollLock
        onClose={closePanel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div ref={panelRef}>
          <Filters
            fieldDefs={fieldDefs}
            initialValues={initialValues}
            description={t('Filters apply at every level below. Objectives that don’t match are hidden.')}
            allowEmptyFiltersList
            onSubmit={handleSubmit}
            onReset={handleReset}
            onClose={closePanel}
            variant="compact"
          />
        </div>
      </StyledPopover>
    </ThemeProvider>
  );
};

export default AlignmentModalFilters;
